import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";

// Import critical components (above the fold)
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import Footer from "./components/Footer";

// Lazy load below-the-fold components for better performance
const AboutSection = lazy(() => import("./components/AboutSection"));
const ProductsSection = lazy(() => import("./components/ProductsSection"));
const ComponentsSection = lazy(() => import("./components/ComponentsSection"));
const ProjectsSection = lazy(() => import("./components/ProjectsSection"));
const InfrastructureSection = lazy(() => import("./components/InfrastructureSection"));
const ContactSection = lazy(() => import("./components/ContactSection"));
const LiftIndicator = lazy(() => import("./components/LiftIndicator"));

// Lazy load Pages (non-critical routes)
const SparesPage = lazy(() => import("./pages/SparesPage"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const ProjectDetailPage = lazy(() => import("./pages/ProjectDetailPage"));

// Lazy load Admin components (rarely accessed)
import { AuthProvider } from "./contexts/AuthContext";
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminComponents = lazy(() => import("./pages/admin/AdminComponents"));
const AdminContacts = lazy(() => import("./pages/admin/AdminContacts"));
const AdminProjects = lazy(() => import("./pages/admin/AdminProjects"));
const AdminSpares = lazy(() => import("./pages/admin/AdminSpares"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-[200px]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a4db3]"></div>
  </div>
);

const Home = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <Suspense fallback={null}>
        <LiftIndicator />
      </Suspense>
      <main>
        <HeroSection />
        <Suspense fallback={<LoadingFallback />}>
          <AboutSection />
          <ProductsSection />
          <ComponentsSection />
          {/* <ProjectsSection /> */}{/* Hidden temporarily - uncomment to show */}
          <InfrastructureSection />
          <ContactSection />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/spares" element={<SparesPage />} />
              <Route path="/projects" element={<ProjectsPage />} />
              <Route path="/projects/:id" element={<ProjectDetailPage />} />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="components" element={<AdminComponents />} />
                <Route path="projects" element={<AdminProjects />} />
                <Route path="spares" element={<AdminSpares />} />
                <Route path="contacts" element={<AdminContacts />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
